'use client';

import { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import EmailLoginInline from '@/components/EmailLoginInline';

/**
 * OnboardingModal — модальный онбординг при клике по кнопке «Начать» в шапке.
 *
 * Поведение:
 * - Desktop (≥640px): centered modal, 2-колоночный layout (маскот full-bleed слева 5/12, контент справа 7/12).
 * - Mobile (<640px): bottom-sheet с drag-down dismiss, маскот сверху ~36vh.
 *
 * Доступность:
 * - role="dialog" + aria-modal + aria-labelledby/describedby
 * - Esc, click on overlay, drag-down (mobile), close button — все закрывают
 * - Body scroll-lock пока открыт
 * - Initial focus на primary CTA; restore focus на trigger при закрытии
 *
 * Анимация (ease-out на enter, ease-in на exit ~70%):
 * - overlay opacity 0→1 200ms / →0 160ms
 * - desktop modal scale 0.96+opacity → 1 240ms / → 0.98 160ms
 * - mobile sheet y "100%" → 0 280ms spring / → "100%" 200ms ease-in
 *
 * Уважает prefers-reduced-motion (только opacity, без transform).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  botUrl: string;
  vkUrl: string;
}

const SHEET_DRAG_DISMISS_THRESHOLD = 120;

// Обе панели (desktop + mobile) живут в DOM одновременно — Tailwind скрывает
// неактивную через display:none. Поэтому ищем активную через offsetParent,
// а не один общий ref (он бы указал на mobile-панель даже при desktop-рендере).
function getVisiblePanel(): HTMLElement | null {
  const panels = document.querySelectorAll<HTMLElement>('[data-onboarding-panel]');
  for (const p of Array.from(panels)) {
    if (p.offsetParent !== null) return p;
  }
  return null;
}

function getFocusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
  );
}

export default function OnboardingModal({ open, onClose, botUrl, vkUrl }: Props) {
  const reduced = useReducedMotion() ?? false;
  const triggerRef = useRef<HTMLElement | null>(null);

  // Сохраняем триггер открытия и возвращаем фокус на close
  useEffect(() => {
    if (open) {
      triggerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    } else if (triggerRef.current) {
      // Возвращаем фокус только если элемент ещё в DOM
      const el = triggerRef.current;
      if (document.body.contains(el)) {
        el.focus();
      }
      triggerRef.current = null;
    }
  }, [open]);

  // Esc + Tab focus-trap (через видимую панель — desktop или mobile)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const panel = getVisiblePanel();
        if (!panel) return;
        const focusables = getFocusables(panel);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Initial focus на primary CTA внутри ВИДИМОЙ панели
  useEffect(() => {
    if (!open) return;
    // requestAnimationFrame чтобы дождаться рендера motion-панели
    const id = requestAnimationFrame(() => {
      const panel = getVisiblePanel();
      panel?.querySelector<HTMLAnchorElement>('a[data-onboarding-primary]')?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  function handleDragEnd(_e: unknown, info: PanInfo) {
    if (info.offset.y > SHEET_DRAG_DISMISS_THRESHOLD || info.velocity.y > 500) {
      onClose();
    }
  }

  // Анимационные пресеты под reduced-motion
  const overlayMotion = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 } }
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] } };

  const desktopMotion = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 } }
    : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.16, ease: [0.4, 0, 1, 1] } },
        transition: { duration: 0.24, ease: [0.16, 1, 0.3, 1] },
      };

  const sheetMotion = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 } }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%', transition: { duration: 0.2, ease: [0.4, 0, 1, 1] } },
        transition: { type: 'spring' as const, stiffness: 320, damping: 32, mass: 0.7 },
      };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="onboarding-overlay"
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4"
          aria-hidden="true"
          onClick={onClose}
          {...overlayMotion}
        >
          {/* DESKTOP: centered modal (flex-родитель центрует — никаких translate'ов, чтобы motion не конфликтовал с Tailwind transform) */}
          <motion.div
            key="onboarding-panel-desktop"
            data-onboarding-panel="desktop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-title"
            aria-describedby="onboarding-desc"
            onClick={(e) => e.stopPropagation()}
            className="relative hidden sm:grid w-full max-w-3xl max-h-[90vh] grid-cols-12 overflow-hidden rounded-3xl bg-[#0d1a0c] border border-white/10"
            style={{ boxShadow: '0 32px 80px -16px rgba(0,0,0,0.6)' }}
            {...desktopMotion}
          >
            <DesktopMascot />
            <PanelContent
              botUrl={botUrl}
              vkUrl={vkUrl}
              variant="desktop"
            />
            <CloseButton onClose={onClose} variant="desktop" />
          </motion.div>

          {/* MOBILE: bottom sheet */}
          <motion.div
            key="onboarding-panel-mobile"
            data-onboarding-panel="mobile"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-title-m"
            aria-describedby="onboarding-desc-m"
            onClick={(e) => e.stopPropagation()}
            drag={reduced ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            className="sm:hidden fixed left-0 right-0 bottom-0 max-h-[92dvh] overflow-y-auto rounded-t-3xl bg-[#0d1a0c] border-t border-white/10"
            style={{ boxShadow: '0 -16px 48px -8px rgba(0,0,0,0.5)', touchAction: 'pan-y' }}
            {...sheetMotion}
          >
            <div className="flex justify-center pt-3 pb-1">
              <span className="block h-1 w-9 rounded-full bg-white/30" aria-hidden="true" />
            </div>
            <MobileMascot />
            <PanelContent
              botUrl={botUrl}
              vkUrl={vkUrl}
              variant="mobile"
            />
            <CloseButton onClose={onClose} variant="mobile" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─────────── Pieces ─────────── */

function DesktopMascot() {
  return (
    <div className="col-span-5 relative overflow-hidden bg-[#0a0a0a]">
      <Image
        src="/landing/mascot-casual.webp"
        alt="Маскот Favbox приглашает начать"
        fill
        sizes="(min-width: 640px) 320px, 0px"
        priority={false}
        className="object-cover object-center scale-105"
      />
      {/* мягкая вертикальная виньетка справа, чтобы граница с контентом не была резкой */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-16 bg-gradient-to-r from-transparent to-[#0d1a0c]"
      />
    </div>
  );
}

function CloseButton({ onClose, variant }: { onClose: () => void; variant: 'desktop' | 'mobile' }) {
  // На мобиле крест лежит ПОВЕРХ маскота — добавляем тёмный backdrop для контраста
  // (без него белая иконка на ярком фрагменте маскота читалась бы плохо).
  // На десктопе крест в правом верхнем углу всей панели — фон уже #0d1a0c.
  const base =
    'absolute z-20 inline-flex items-center justify-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40';
  const desktopCls = `${base} top-3 right-3 w-10 h-10 text-white/60 hover:text-white hover:bg-white/10`;
  const mobileCls = `${base} top-4 right-4 w-10 h-10 text-white/85 hover:text-white bg-black/35 hover:bg-black/55 backdrop-blur-sm`;
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Закрыть"
      className={variant === 'desktop' ? desktopCls : mobileCls}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );
}

function MobileMascot() {
  return (
    <div className="relative w-full h-[34vh] min-h-[200px] overflow-hidden bg-[#0a0a0a]">
      <Image
        src="/landing/mascot-casual.webp"
        alt="Маскот Favbox приглашает начать"
        fill
        sizes="100vw"
        priority={false}
        className="object-cover object-top"
      />
      {/* плавный переход в фон контента снизу */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-[#0d1a0c]"
      />
    </div>
  );
}

function PanelContent({
  botUrl,
  vkUrl,
  variant,
}: {
  botUrl: string;
  vkUrl: string;
  variant: 'desktop' | 'mobile';
}) {
  const isDesktop = variant === 'desktop';
  const titleId = isDesktop ? 'onboarding-title' : 'onboarding-title-m';
  const descId = isDesktop ? 'onboarding-desc' : 'onboarding-desc-m';

  return (
    <div
      className={
        isDesktop
          ? 'col-span-7 relative p-8 lg:p-10 flex flex-col gap-5'
          : 'relative px-6 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex flex-col gap-4'
      }
    >
      <h2
        id={titleId}
        className="font-display font-bold text-white leading-[0.98] tracking-[-0.02em] pr-10"
        style={{ fontSize: isDesktop ? 'clamp(1.75rem, 3vw, 2.5rem)' : 'clamp(1.5rem, 6vw, 2rem)' }}
      >
        Как <span className="text-[#f5a85a]">начать.</span>
      </h2>

      <p
        id={descId}
        className="font-body text-white/75 leading-relaxed max-w-md"
        style={{ fontSize: isDesktop ? '1rem' : '0.95rem' }}
      >
        Открой бот в&nbsp;Telegram или VK и&nbsp;кинь любую ссылку — фильм, рецепт, статью.
        Полку выбираешь одним нажатием, а&nbsp;на&nbsp;сайте спокойно разберёшь.
      </p>

      <div className={isDesktop ? 'flex flex-col gap-3 mt-1' : 'flex flex-col gap-2.5 mt-1'}>
        <Link
          data-onboarding-primary="true"
          href={botUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          style={{ boxShadow: '0 12px 32px -8px rgba(183,16,42,0.55)' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.48 14.49l-2.95-.924c-.64-.203-.654-.64.136-.95l11.52-4.44c.535-.194 1.003.13.376.072z" />
          </svg>
          Открыть в Telegram
        </Link>
        <Link
          href={vkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-[#0077FF] hover:bg-[#0066DD] active:scale-[0.98] text-white font-semibold text-base transition-all cursor-pointer font-body focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          style={{ boxShadow: '0 12px 32px -8px rgba(0,119,255,0.55)' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12.077 17c-6.237 0-9.808-4.274-9.957-11.377H5.06c.105 5.213 2.507 7.418 4.407 7.872V5.623h2.72v4.494c1.876-.202 3.848-2.34 4.515-4.494h2.72c-.51 2.658-2.65 4.796-4.17 5.633 1.52.685 3.93 2.571 4.853 5.744h-2.995c-.72-2.24-2.51-3.971-4.923-4.208v4.208h-.11z" />
          </svg>
          Открыть в VK
        </Link>
      </div>

      <p className="font-mono text-white/55 text-[11px] uppercase tracking-widest mt-1">
        бесплатно · запуск за секунду
      </p>

      <EmailLoginInline />
    </div>
  );
}
